import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Package, Edit } from 'lucide-react';

interface Bundle {
  id: string;
  bundle_id: string;
  bundle_name: string;
  description: string | null;
  included_features: string[];
  price_monthly: number;
  price_yearly: number | null;
  discount_percentage: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface FeatureOption {
  feature_id: string;
  feature_name: string;
}

export default function BundlesManager() {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<FeatureOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<Bundle | null>(null);

  // Form state
  const [bundleId, setBundleId] = useState('');
  const [bundleName, setBundleName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [priceMonthly, setPriceMonthly] = useState('');
  const [priceYearly, setPriceYearly] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    loadBundles();
    loadFeatures();
  }, []);

  const loadBundles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_bundles')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setBundles(data || []);
    } catch (error) {
      console.error('Error loading bundles:', error);
      toast.error('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  const loadFeatures = async () => {
    try {
      const { data, error } = await supabase
        .from('feature_pricing')
        .select('feature_id, feature_name')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setAvailableFeatures(data || []);
    } catch (error) {
      console.error('Error loading features:', error);
    }
  };

  const resetForm = () => {
    setBundleId('');
    setBundleName('');
    setDescription('');
    setSelectedFeatures([]);
    setPriceMonthly('');
    setPriceYearly('');
    setDiscountPercentage(0);
    setIsActive(true);
    setSortOrder(0);
    setEditingBundle(null);
  };

  const handleEdit = (bundle: Bundle) => {
    setEditingBundle(bundle);
    setBundleId(bundle.bundle_id);
    setBundleName(bundle.bundle_name);
    setDescription(bundle.description || '');
    setSelectedFeatures(bundle.included_features);
    setPriceMonthly(bundle.price_monthly.toString());
    setPriceYearly(bundle.price_yearly?.toString() || '');
    setDiscountPercentage(bundle.discount_percentage);
    setIsActive(bundle.is_active);
    setSortOrder(bundle.sort_order);
    setIsDialogOpen(true);
  };

  const handleSaveBundle = async () => {
    if (!bundleId || !bundleName || !priceMonthly || selectedFeatures.length === 0) {
      toast.error('Please fill in all required fields and select at least one feature');
      return;
    }

    try {
      const bundleData = {
        bundle_id: bundleId,
        bundle_name: bundleName,
        description: description || null,
        included_features: selectedFeatures,
        price_monthly: parseFloat(priceMonthly),
        price_yearly: priceYearly ? parseFloat(priceYearly) : null,
        discount_percentage: discountPercentage,
        is_active: isActive,
        sort_order: sortOrder,
      };

      if (editingBundle) {
        const { error } = await supabase
          .from('subscription_bundles')
          .update(bundleData)
          .eq('id', editingBundle.id);

        if (error) throw error;
        toast.success('Bundle updated successfully');
      } else {
        const { error } = await supabase
          .from('subscription_bundles')
          .insert([bundleData]);

        if (error) throw error;
        toast.success('Bundle created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      loadBundles();
    } catch (error) {
      console.error('Error saving bundle:', error);
      toast.error('Failed to save bundle');
    }
  };

  const handleDeleteBundle = async (id: string) => {
    if (!confirm('Are you sure you want to delete this bundle?')) return;

    try {
      const { error } = await supabase
        .from('subscription_bundles')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Bundle deleted successfully');
      loadBundles();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      toast.error('Failed to delete bundle');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('subscription_bundles')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast.success(`Bundle ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadBundles();
    } catch (error) {
      console.error('Error toggling bundle status:', error);
      toast.error('Failed to update bundle status');
    }
  };

  const toggleFeature = (featureId: string) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />
            Subscription Bundles
          </h2>
          <p className="text-muted-foreground mt-1">
            Create and manage feature bundles with discounted pricing
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Bundle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBundle ? 'Edit Bundle' : 'Create New Bundle'}</DialogTitle>
              <DialogDescription>
                Create bundled pricing for multiple features
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bundle-id">Bundle ID *</Label>
                  <Input
                    id="bundle-id"
                    value={bundleId}
                    onChange={(e) => setBundleId(e.target.value)}
                    placeholder="growth"
                    disabled={!!editingBundle}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bundle-name">Bundle Name *</Label>
                  <Input
                    id="bundle-name"
                    value={bundleName}
                    onChange={(e) => setBundleName(e.target.value)}
                    placeholder="Growth"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Comprehensive personal development"
                />
              </div>
              <div className="space-y-2">
                <Label>Included Features *</Label>
                <div className="border rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                  {availableFeatures.map(feature => (
                    <div key={feature.feature_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={feature.feature_id}
                        checked={selectedFeatures.includes(feature.feature_id)}
                        onCheckedChange={() => toggleFeature(feature.feature_id)}
                      />
                      <Label htmlFor={feature.feature_id} className="cursor-pointer">
                        {feature.feature_name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price-monthly">Monthly Price (£) *</Label>
                  <Input
                    id="price-monthly"
                    type="number"
                    step="0.01"
                    value={priceMonthly}
                    onChange={(e) => setPriceMonthly(e.target.value)}
                    placeholder="49.99"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price-yearly">Yearly Price (£)</Label>
                  <Input
                    id="price-yearly"
                    type="number"
                    step="0.01"
                    value={priceYearly}
                    onChange={(e) => setPriceYearly(e.target.value)}
                    placeholder="499.99"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discount">Discount % (compared to individual)</Label>
                  <Input
                    id="discount"
                    type="number"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sort-order">Sort Order</Label>
                  <Input
                    id="sort-order"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is-active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="is-active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveBundle}>
                {editingBundle ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bundle</TableHead>
              <TableHead>Features</TableHead>
              <TableHead>Monthly</TableHead>
              <TableHead>Yearly</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bundles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No bundles found
                </TableCell>
              </TableRow>
            ) : (
              bundles.map((bundle) => (
                <TableRow key={bundle.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{bundle.bundle_name}</div>
                      <div className="text-sm text-muted-foreground">{bundle.bundle_id}</div>
                      {bundle.description && (
                        <div className="text-sm text-muted-foreground mt-1">{bundle.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {bundle.included_features.map(feature => {
                        const featureInfo = availableFeatures.find(f => f.feature_id === feature);
                        return featureInfo?.feature_name || feature;
                      }).join(', ')}
                    </div>
                  </TableCell>
                  <TableCell>£{bundle.price_monthly.toFixed(2)}</TableCell>
                  <TableCell>
                    {bundle.price_yearly ? `£${bundle.price_yearly.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>{bundle.discount_percentage}%</TableCell>
                  <TableCell>
                    <Switch
                      checked={bundle.is_active}
                      onCheckedChange={() => handleToggleActive(bundle.id, bundle.is_active)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(bundle)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBundle(bundle.id)}
                        className="hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
